import { useMemo } from 'react'
import { IProblemLineNew } from '../../../../../modules/API/TeudotProblems/models/problems'
import { useProblemTypes } from './useProblemTypes'
import { LOAD, LOAD_END } from '../../../../../../store/actions/actionsTypes'
import { useDispatch, useSelector } from 'react-redux'
import { GET_PDF_TEUDOT } from '../../../../../modules/API/settings'
import { RootState } from '../../../../../../store/reducers/rootReducer'

// Keep the old hardcoded array as fallback
const FALLBACK_PROBLEM_TYPES = [
  { value: 1, label: 'נזק מכני' },
  { value: 2, label: 'חסר במשקל' },
  { value: 3, label: 'בעיית איכות' },
  { value: 4, label: 'נזק טמפרטורה' },
  { value: 5, label: 'בעיית אריזה' },
  { value: 6, label: 'זן שגוי' },
  { value: 7, label: 'גודל לא תואם' },
  { value: 8, label: 'זיהום' },
  { value: 9, label: 'בעיית תפוגה' },
  { value: 10, label: 'עיכוב במשלוח' }
] as const

export const problemStatuses = {
  0: 'פתוח',
  1: 'אושר',
  2: 'ביטול',
  3: 'בטיפול',
} as const

export const statusListForSelect = Object.entries(problemStatuses).map(([key, value]) => ({
  id: +key,
  desc: value === "אושר" ? "אישור" : value === "ביטול" ? "דחיה" : value
})) as {id: number, desc: string}[]

export const useProblemTypesFormatted = () => {
  const { problemTypes, isLoading } = useProblemTypes()
  
  const formattedTypes = useMemo(() => {
    if (!problemTypes) return FALLBACK_PROBLEM_TYPES
    
    return problemTypes.map(type => ({
      value: type.Code,
      label: type.Description,
      priceAllowed: type.PriceAllowedToFill,
      quantityAllowed: type.QuantityAllowedToFill,
      weightAllowed: type.WeightAllowedToFill
    }))
  }, [problemTypes])
  
  return {
    problemTypes: formattedTypes,
    isLoading
  }
}
const driverStatuses = {
  0: 'פתוח',
  1: 'נאסף',
  2: 'לא נאסף'
}
export const useProblemTypeLabel = () => {
  const { problemTypes } = useProblemTypes()
  
  return (type: number): string => {
    if (!problemTypes) {
      // Fallback to hardcoded types if Redux data isn't available
      const fallbackType = FALLBACK_PROBLEM_TYPES.find(pt => pt.value === type)
      return fallbackType?.label || 'פתוח'
    }
    
    const problemType = problemTypes.find(pt => pt.Code === type)
    return problemType?.Description || 'פתוח'
  }
}

export const getProblemTypeLabel = (type: number): string => {
  // This will be used with the hook in components
  const fallbackType = FALLBACK_PROBLEM_TYPES.find(pt => pt.value === type)
  return fallbackType?.label || 'פתוח'
}

export const getTextFromDriverStatus = (status: number): string => {
    return driverStatuses[status as keyof typeof driverStatuses] || 'פתוח'
}

export const getDriverStatusColor = (status: number): string => {
  const text = getTextFromDriverStatus(status) || ''
  return text === 'נאסף' ? '#80E081' : 
  text === 'לא נאסף' ? 'tomato' :
  '#8A97AC'
}

export const getStatusText = (status: number): string => {
  switch (status) {
    case 1: return 'אושר'
    case 2: return 'ביטול'
    case 3: return 'panding'
    
    default: return 'פתוח'
  }
}

export const getStatusColor = (status: number): string => {
  switch (status) {
    case 1: return '#32CD32' // Orange - waiting
    case 2: return 'tomato' // Blue - in progress
    case 3: return 'orange' // 
    
    default: return '#808080' // Gray - open/unknown
  }
}

interface UseProblemHelpersReturn {
  // Basic checks
  hasProblems: boolean
  
  // Status calculations
  overallStatus: number | null
  statusCounts: {
    waiting: number
    approved: number
    rejected: number
    total: number
    panding: number
  }
  
  // Display values
  displayColor: string
  displayStatusText: string
  
  // Helper functions
  getProblemTypeLabel: (type: number) => string
  getStatusText: (status: number) => string
  printPdf: (code: number, sub: number) => Promise<void>

}

export const useProblemHelpers = (problems: IProblemLineNew[] = []): UseProblemHelpersReturn => {
  const dispatch = useDispatch()
  const token = useSelector((state: RootState) => state.mainReducer.token)
  const hasProblems = problems && problems?.length > 0
  const getProblemTypeLabelHook = useProblemTypeLabel()
  const printPdf = async (code: number, sub: number) => {
 
    dispatch({ type: LOAD });
  
    try {
      const resp  = await fetch(
        `${GET_PDF_TEUDOT}deliveryNum=${code}&deliverySub=${sub}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/pdf'            // не обязательно, но корректно
          }
        }
      );
  
      const blob   = await resp.blob();                       // PDF‑поток
      const file   = new Blob([blob], { type: 'application/pdf' });
      const url    = URL.createObjectURL(file);
  
      // открываем во вкладке / системном PDF‑просмотрщике
      const win = window.open(url, '_blank');
  
      //  освободить память, когда вкладка подгрузила файл
      if (win) {
        win.addEventListener('load', () => URL.revokeObjectURL(url));
      } else {
        // если pop‑up заблокирован — fallback: скачать
        const a = document.createElement('a');
        a.href = url;
        a.download = `תעודה-${code}-${sub}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error(e);
    } finally {
      dispatch({ type: LOAD_END });
    }
  };
  const statusCounts = useMemo(() => {
    const counts = {
      waiting: 0,
      rejected: 0, 
      approved: 0,
      panding: 0,
      total: problems?.length || 0
    }

    problems?.forEach(problem => {
      switch (problem.Status) {
        case 1:
          counts.approved++
          break
        case 2:
          counts.rejected++
          break
        case 3:
          counts.panding++
          break
        case 0:
          counts.waiting++
          break
      }
    })

    return counts
  }, [problems])

  const overallStatus = useMemo(() => {
    if (!hasProblems) return null
    
    // Priority: approved > inProgress > waiting
    if (statusCounts.waiting > 0) return 0
    if (statusCounts.approved > 0) return 1
    if (statusCounts.rejected > 0) return 2
    if (statusCounts.panding > 0) return 3
    
    return 0 // default
  }, [hasProblems, statusCounts])

  const displayColor = useMemo(() => {
    if (!hasProblems) return '#32CD32' // Green for no problems
    return getStatusColor(overallStatus || 0)
  }, [hasProblems, overallStatus])
  
  const displayStatusText = useMemo(() => {
    if (!hasProblems) return 'ללא פניות'
    return getStatusText(overallStatus || 0)
  }, [hasProblems, overallStatus])

  return {
    hasProblems,
    overallStatus,
    statusCounts,
    displayColor,
    displayStatusText,
    getProblemTypeLabel: getProblemTypeLabelHook,
    getStatusText,
    printPdf
  }
} 